import { Flex, Form, FormInstance, Modal, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddCoordinates, { CordsProps } from './AddCoordinates';
import AddCustomerDetails from './AddCustomerDetails';
import AddLocationInfo from './AddLocationInfo';
import { AddIcon } from '../Icons';
import { Heading } from '../Heading';
import usePickupAddressForm from '@/hooks/usePickupAddressForm';
import { usePickupAddressStore } from '@/stores';

interface ModalProps {
  visible: boolean;
  onClose: () => void;
  updatePreviewOrderPrice?: (pickupAddressUID?: string) => void;
}

const AddNewLocationModal: React.FC<ModalProps> = ({
  visible,
  onClose,
  updatePreviewOrderPrice,
}) => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const { pickupAddresses } = usePickupAddressStore();

  const [coordinates, setCoordinates] = useState<CordsProps | null>(null);

  const handleCoordinatesChange = (coords: CordsProps) => {
    setCoordinates(coords);
  };

  useEffect(() => {
    if (!pickupAddresses) {
      setCoordinates(null);
    }
  }, [pickupAddresses]);

  const {
    form,
    onFinish,
    loadingCreatePickup,
    loadingUpdatePickup,
    loadingSinglePickupAddress,
    isFetching,
    isLoading,
    isPending,
  } = usePickupAddressForm({
    coordinates,
    setCoordinates,
    visible,
    onClose,
  });

  const handleSubmit = async (values: any) => {
    setSubmitted(true);
    const pickupAddressUID = await onFinish?.(values);

    updatePreviewOrderPrice?.(pickupAddressUID ?? undefined);
  };

  return (
    <Modal
      className='rounded-[20px] bg-white border border-customGrayShade3'
      title={
        <Flex
          className='font-bold text-lg leading-5  uppercase text-customTunaPrimary '
          style={{ borderBottom: '1px solid #E1E1E3', paddingBottom: '20px' }}
        >
          {pickupAddresses === null ? t('add_new_location') : t('edit_location')}
        </Flex>
      }
      width={709}
      centered
      open={visible}
      footer={null}
      onClose={onClose}
      onCancel={onClose}
    >
      {isPending ? (
        <Spin className='my-10 mx-auto block' />
      ) : (
        <Form form={form} onFinish={handleSubmit} initialValues={{ type: 'WAREHOUSE' }}>
          <AddCustomerDetails
            form={form}
            title={t('contact_info')}
            firstNamePlaceholder={t('name')}
            primaryPhonePlaceholder={t('primary_phone')}
          />
          <AddLocationInfo
            title={t('location_info')}
            locationNamePlaceholder={t('location_name')}
            regionPlaceholder={t('province')}
            cityPlaceholder={t('area')}
            addressPlaceholder={t('address')}
            typePlaceholder={t('type')}
            submitted={submitted}
            form={form}
          />
          <AddCoordinates
            title={t('location_coordinates')}
            searchPlaceholder={t('search_google_maps')}
            onCoordinatesChange={handleCoordinatesChange}
            coordinates={coordinates}
          />
          <Flex className='flex justify-end w-full gap-x-2'>
            <button
              type='button'
              onClick={onClose}
              className='flex items-center gap-1.5 h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(245,245,243)] transition-colors uppercase'
            >
              {t('cancel')}
            </button>
            <button
              type='submit'
              className='flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors uppercase'
              disabled={loadingCreatePickup || loadingUpdatePickup || isPending}
            >
              {(loadingCreatePickup || loadingUpdatePickup || isPending) ? (
                <span className='h-3.5 w-3.5 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
              ) : (
                <AddIcon width={18} height={18} />
              )}
              {pickupAddresses === null ? t('add_location') : t('edit_location')}
            </button>
          </Flex>
        </Form>
      )}
    </Modal>
  );
};

export default AddNewLocationModal;
